/* eslint-disable max-lines */

import { UUID } from '@litlingo/react';
import {
  addNewLanguageMatcher,
  fetchIdentifierCustomers,
  fetchLanguageMatcherTypes,
  selectCustomerIdentifier,
  setActiveLanguageMatcherId,
  setCustomerGroupIdentifier,
  setPromptCompareMode,
} from 'actions/identifier';
import CollapsableSection from 'components/CollapsableSection';
import AddPromptBlockModal from 'components/IdentifierName/LLMCategorizer/AddPromptBlockModal';
import LanguageMatcherSidebarList from 'components/LanguageMatcherRedesign/LanguageMatcherSidebarList';
import SelectRedesign from 'components/Select/SelectRedesign';
import TestSentenceVisualizerSidebarCategorizer from 'components/TestSentenceVisualizer/TestSentenceVisualizerSidebarCategorizer';
import TestSentenceVisualizerSidebarIdentifiers from 'components/TestSentenceVisualizer/TestSentenceVisualizerSidebarIdentifiers';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  getActiveLanguageMatcher,
  getCustomerGroup,
  getHasStaging,
  getIdentifierCustomers,
  getLanguageMatcherFromSelectedIdentifier,
  getPromptCompareMode,
  getSelectedCustomerIdentifier,
  getSelectedIdentifierRevision,
} from 'selectors/identifier';
import { useSelector } from 'store';
import type { NormalizedIdentifier } from 'types';
import { v4 as uuidv4 } from 'uuid';
import IdentifierHeader from '../IdentifierHeader';
import CategorizerScopeSection from './CategorizerScopeSection';
import IdentifierManagerUtilizationSection from './IdentifierManagerUtilizationSection';

type ComponentProps = {
  identifier: NormalizedIdentifier;
};

const IdentifierSidebar: React.FC<ComponentProps> = ({ identifier }) => {
  const dispatch = useDispatch();
  const [languageMatcherFilter, setLanguageMatcherFilter] = useState('');
  const [isPromptModalOpen, setIsPromptModalOpen] = useState(false);

  const activeLanguageMatcher = useSelector(getActiveLanguageMatcher);
  const identifierRevision = useSelector(getSelectedIdentifierRevision);

  const languageMatchers = useSelector((state) =>
    getLanguageMatcherFromSelectedIdentifier(
      state,
      languageMatcherFilter.toUpperCase(),
      /* @ts-ignore */
      identifierRevision?.kind !== 'llm_categorizer'
    )
  );
  const customers = useSelector(getIdentifierCustomers);
  const selectedCustomer = useSelector(getSelectedCustomerIdentifier);
  const customerGroup = useSelector(getCustomerGroup);
  const hasStaging = useSelector(getHasStaging);
  const promptCompareMode = useSelector(getPromptCompareMode);

  useEffect(() => {
    dispatch(fetchLanguageMatcherTypes());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dispatch(setActiveLanguageMatcherId(languageMatchers[0]?.uuid));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (identifierRevision?.uuid) {
      dispatch(fetchIdentifierCustomers(identifierRevision.uuid));
    }
  }, [dispatch, identifierRevision?.uuid]);

  const handleLanguageMatcherClick = (languageMatcherId: UUID): void => {
    if (promptCompareMode.isComparing) {
      if (activeLanguageMatcher?.uuid === languageMatcherId) {
        if (promptCompareMode.secondaryMatcher) {
          dispatch(setActiveLanguageMatcherId(promptCompareMode.secondaryMatcher));
          dispatch(setPromptCompareMode({ isComparing: true, secondaryMatcher: '' }));
          return;
        }
        dispatch(setActiveLanguageMatcherId(''));
        dispatch(setPromptCompareMode({ isComparing: false, secondaryMatcher: '' }));
        return;
      }

      if (promptCompareMode.secondaryMatcher === languageMatcherId) {
        dispatch(setPromptCompareMode({ isComparing: true, secondaryMatcher: '' }));
        return;
      }

      dispatch(setPromptCompareMode({ isComparing: true, secondaryMatcher: languageMatcherId }));
      return;
    }

    if (activeLanguageMatcher && activeLanguageMatcher.uuid === languageMatcherId) {
      dispatch(setActiveLanguageMatcherId(''));
    } else {
      dispatch(setActiveLanguageMatcherId(languageMatcherId));
    }
    dispatch(selectCustomerIdentifier(''));
  };

  const toggleAddPromptBlockModal = (): void => {
    setIsPromptModalOpen(!isPromptModalOpen);
  };

  const addLocalLanguageMatcher = (): void => {
    if (!identifierRevision) return;
    // @ts-ignore
    if (identifierRevision.kind === 'llm_categorizer') {
      toggleAddPromptBlockModal();
      return;
    }
    const newMatcherId = uuidv4();
    const matcherBody = {
      uuid: newMatcherId,
      name: `New ${languageMatchers.length + 1}`,
      saved: false,
      isNew: true,
      type: 'token',
    };

    dispatch(
      addNewLanguageMatcher({
        identifierId: identifierRevision.uuid,
        languageMatcherId: newMatcherId,
        matcherBody,
      })
    );
    dispatch(setActiveLanguageMatcherId(newMatcherId));
  };

  const handleSelectCustomerGroup = (option: string): void => {
    dispatch(setCustomerGroupIdentifier(option));
  };

  const renderCustomerGroup = (): JSX.Element | null => {
    let customerGroupOptions = [{ label: 'Production', value: 'production' }];
    if (hasStaging) {
      customerGroupOptions = [{ label: 'Staging', value: 'staging' }, ...customerGroupOptions];
    }

    return (
      <div className="w-28 self-end">
        <SelectRedesign
          className="bg-white text-body"
          onChange={handleSelectCustomerGroup}
          options={customerGroupOptions}
          value={customerGroup}
        />
      </div>
    );
  };

  const handleHeaderCardClick = (): void => {
    // @ts-ignore
    if (identifierRevision?.kind === 'llm_categorizer' && !promptCompareMode.isComparing) {
      dispatch(setActiveLanguageMatcherId(''));
      dispatch(selectCustomerIdentifier(''));
    }
  };

  if (!identifierRevision) return null;

  return (
    <>
      <div className="sticky top-0 z-100 shadow-header-shadow">
        <CollapsableSection
          headerTitle={identifier.name}
          headerClasses="w-full p-4 flex flex-row justify-between items-center text-heading-3 bg-litlingo-primary-120 border-l border-t border-litlingo-gray-1"
          titleClasses="text-white"
          iconColorClass="white"
          contentClasses="p-0"
        >
          <button
            type="button"
            className={`identifier-card-hover flex flex-col focus:outline-none ${
              // @ts-ignore
              identifierRevision?.kind === 'llm_categorizer' ? '' : 'cursor-default'
            }`}
            onClick={handleHeaderCardClick}
          >
            <IdentifierHeader />
          </button>
        </CollapsableSection>
      </div>
      {/* @ts-ignore */}
      {identifierRevision.kind === 'llm_categorizer' ? (
        <CollapsableSection
          headerTitle="Scope"
          headerClasses="w-full p-4 flex flex-row justify-between items-center text-heading-3 bg-litlingo-primary-120 border-l border-litlingo-gray-1"
          titleClasses="text-white truncate"
          iconColorClass="white"
          contentClasses="p-0"
          isDefaultClosed
        >
          <CategorizerScopeSection />
        </CollapsableSection>
      ) : (
        <CollapsableSection
          headerTitle={`Utilization${
            selectedCustomer ? ` - ${customers.find((c) => c.uuid === selectedCustomer)?.name}` : ''
          }`}
          headerClasses="w-full p-4 flex flex-row justify-between items-center text-heading-3 bg-litlingo-primary-120 border-l border-litlingo-gray-1"
          titleClasses="text-white truncate"
          iconColorClass="white"
          contentClasses="p-0"
          isDefaultClosed
        >
          <div
            className={`flex flex-col gap-4 ${selectedCustomer ? 'bg-litlingo-success-light' : ''}`}
          >
            <IdentifierManagerUtilizationSection />
          </div>
        </CollapsableSection>
      )}
      <CollapsableSection
        /* @ts-ignore */
        headerTitle={identifierRevision?.kind === 'llm_categorizer' ? 'Prompt Blocks' : 'Matchers'}
        headerClasses="w-full p-4 flex flex-row justify-between items-center text-heading-3 bg-litlingo-primary-120 border-l border-litlingo-gray-1"
        titleClasses="text-white"
        iconColorClass="white"
        contentClasses="p-0"
        extraAction={renderCustomerGroup()}
      >
        <LanguageMatcherSidebarList
          languageMatchers={languageMatchers}
          languageMatcherFilter={languageMatcherFilter}
          setLanguageMatcherFilter={setLanguageMatcherFilter}
          highlightedLanguageMatcher={activeLanguageMatcher?.uuid || ''}
          addLocalLanguageMatcher={addLocalLanguageMatcher}
          handleLanguageMatcherClick={handleLanguageMatcherClick}
          identifierRevision={identifierRevision}
        />
      </CollapsableSection>
      {/* <CollapsableSection
        headerTitle="Test"
        headerClasses="w-full p-4 flex flex-row justify-between items-center text-heading-3 bg-litlingo-primary-120 border-b border-l border-litlingo-gray-1"
        titleClasses="text-white"
        iconColorClass="white"
        isDefaultClosed
        contentClasses="p-0"
      >
        <TestPhraseSidebarSection />
      </CollapsableSection> */}
      <CollapsableSection
        headerTitle="Test"
        headerClasses="w-full p-4 flex flex-row justify-between items-center text-heading-3 bg-litlingo-primary-120 border-l border-litlingo-gray-1"
        titleClasses="text-white"
        iconColorClass="white"
        contentClasses="p-0"
      >
        {/* @ts-ignore */}
        {identifier.kind === 'llm_categorizer' ? (
          <TestSentenceVisualizerSidebarCategorizer identifierId={identifier.uuid} />
        ) : (
          <TestSentenceVisualizerSidebarIdentifiers identifierId={identifier.uuid} />
        )}
      </CollapsableSection>
      {isPromptModalOpen && (
        <AddPromptBlockModal
          isModalOpen={isPromptModalOpen}
          toggleModalOpen={toggleAddPromptBlockModal}
        />
      )}
    </>
  );
};

export default IdentifierSidebar;
